// src/components/TaskModal.jsx
// Full CRUD modal for task interaction

import React, { useState, useEffect } from 'react';
import './TaskModal.css';
import { apiFetch } from '../lib/api';

const PRIORITY_OPTIONS = ['Urgent', 'High', 'Medium', 'Low'];
const STATUS_OPTIONS   = ['Suggested', 'Approved', 'In Progress', 'Snoozed'];
const TYPE_OPTIONS     = ['Follow-up', 'Meeting Prep', 'Research', 'Proposal Work', 'Administrative'];
const TIME_OPTIONS     = ['15 min', '30 min', '45 min', '1 hour'];

export default function TaskModal({ task, onClose, onSave, onDelete, onNavigateAccount }) {
  const [form, setForm]       = useState({ ...task });
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError]     = useState(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/tasks/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId: form.id,
          name:       form.name,
          priority:   form.priority,
          status:     form.status,
          taskType:   form.taskType,
          timeNeeded: form.timeNeeded,
          dueDate:    form.dueDate || null,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed');
      setSaved(true);
      if (onSave) onSave(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDone = async () => {
    setForm(prev => ({ ...prev, status: 'Done' }));
    setSaving(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/tasks/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: form.id, status: 'Done' }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || 'Update failed');
      setSaved(true);
      // Retire from list immediately — call onDelete so task disappears without refresh
      setTimeout(() => {
        if (onDelete) onDelete(form.id);
        onClose();
      }, 600);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/tasks/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: form.id }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || 'Delete failed');
      if (onDelete) onDelete(form.id);
      onClose();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const priorityColor = { 'Urgent': '#ef4444', 'High': '#f97316', 'Medium': '#f59e0b', 'Low': 'var(--dm-blue-mid)' }[form.priority] || 'var(--dm-grey-600)';
  const isDone = form.status === 'Done';

  return (
    <div className="tm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tm-modal">

        {/* -- Header ------------------------------- */}
        <div className="tm-header">
          <div className="tm-header-left">
            <div className="tm-icon">✅</div>
            <div>
              <div className="tm-header-label">Task</div>
              <div className="tm-status-badge" style={{ background: isDone ? 'var(--dm-green-soft)' : priorityColor }}>
                {isDone ? 'Done' : form.priority}
              </div>
            </div>
          </div>
          <button className="tm-close" onClick={onClose}>✕</button>
        </div>

        {/* -- Task Name ----------------------------- */}
        <div className="tm-section">
          <label className="tm-label">Task Name</label>
          <textarea
            className="tm-textarea"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            rows={2}
            disabled={isDone}
          />
        </div>

        {/* -- Fields Grid --------------------------- */}
        <div className="tm-grid">

          <div className="tm-field">
            <label className="tm-label">Priority</label>
            <select className="tm-select" value={form.priority || ''} onChange={(e) => set('priority', e.target.value)} disabled={isDone}>
              <option value="">-- none --</option>
              {PRIORITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="tm-field">
            <label className="tm-label">Status</label>
            <select className="tm-select" value={form.status || ''} onChange={(e) => set('status', e.target.value)}>
              <option value="">-- none --</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="tm-field">
            <label className="tm-label">Task Type</label>
            <select className="tm-select" value={form.taskType || ''} onChange={(e) => set('taskType', e.target.value)} disabled={isDone}>
              <option value="">-- none --</option>
              {TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="tm-field">
            <label className="tm-label">Time Needed</label>
            <select className="tm-select" value={form.timeNeeded || ''} onChange={(e) => set('timeNeeded', e.target.value)} disabled={isDone}>
              <option value="">-- none --</option>
              {TIME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="tm-field tm-field-full">
            <label className="tm-label">Due Date</label>
            <input
              type="date"
              className="tm-input"
              value={form.dueDate ? form.dueDate.slice(0, 10) : ''}
              onChange={(e) => set('dueDate', e.target.value || null)}
              disabled={isDone}
            />
          </div>

        </div>

        {/* -- Linked Account ------------------------ */}
        {(form.accountName || form.accountId) && (
          <div className="tm-section">
            <label className="tm-label">Linked Account</label>
            <div
              className="tm-account-link"
              onClick={() => { if (onNavigateAccount && form.accountId) { onNavigateAccount(form.accountId); onClose(); } }}
            >
              <span className="tm-account-avatar">{(form.accountName || 'A')[0].toUpperCase()}</span>
              <span className="tm-account-name">{form.accountName || 'View Account'}</span>
              <span className="tm-account-arrow">→</span>
            </div>
          </div>
        )}

        {/* -- Error --------------------------------- */}
        {error && <div className="tm-error">⚠ {error}</div>}

        {/* -- Actions ------------------------------- */}
        <div className="tm-actions">
          <div className="tm-actions-left">
            <button
              className={`tm-btn tm-btn-delete ${confirmDelete ? 'tm-btn-confirm' : ''}`}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : confirmDelete ? 'Confirm Delete?' : '🗑 Delete'}
            </button>
            {confirmDelete && (
              <button className="tm-btn tm-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
            )}
          </div>
          <div className="tm-actions-right">
            {!isDone && (
              <button className="tm-btn tm-btn-done" onClick={handleMarkDone} disabled={saving}>
                {saving ? '…' : '✓ Mark Done'}
              </button>
            )}
            <button className="tm-btn tm-btn-save" onClick={handleSave} disabled={saving || isDone}>
              {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Changes'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
